import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import {
  ChevronRight,
  Shield,
  AlarmCheck,
  BookOpen,
  Award,
  Sparkles,
  Star,
  Trophy,
  Heart,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

// Keyword-based icon picker for takeaway cards
const TAKEAWAY_ICONS = [
  { keywords: ['güvenlik', 'güvenli', 'safety', 'safe', 'koruma', 'protect'], Icon: Shield, color: 'text-sky-500', bg: 'bg-sky-50 border-sky-200' },
  { keywords: ['acil', 'hayat', 'emergency', 'alarm', 'tahliye', 'evacuat', 'kurtarma'], Icon: AlarmCheck, color: 'text-rose-500', bg: 'bg-rose-50 border-rose-200' },
  { keywords: ['eğitim', 'öğren', 'train', 'learn', 'bilgi', 'knowledge', 'ders', 'study'], Icon: BookOpen, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200' },
  { keywords: ['sorumluluk', 'disiplin', 'profes', 'award', 'başarı', 'achievement', 'kalite'], Icon: Award, color: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-200' },
];

const FALLBACK_ICONS = [
  { Icon: Sparkles, color: 'text-violet-500', bg: 'bg-violet-50 border-violet-200' },
  { Icon: Star, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200' },
  { Icon: Trophy, color: 'text-sky-500', bg: 'bg-sky-50 border-sky-200' },
  { Icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50 border-rose-200' },
];

function pickIconForTakeaway(text, idx) {
  const lower = (text || '').toLowerCase();
  const matched = TAKEAWAY_ICONS.find(({ keywords }) =>
    keywords.some((kw) => lower.includes(kw))
  );
  if (matched) return matched;
  return FALLBACK_ICONS[idx % FALLBACK_ICONS.length];
}

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.22, delay: i * 0.07 } }),
};

export default function IntroPhase({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'intro-phase',
}) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();

  const content = phase?.content || {};

  const titleText =
    lang === 'tr'
      ? (phase?.title?.tr || phase?.title_tr || '')
      : (phase?.title?.en || phase?.title_en || phase?.title?.tr || '');

  const bodyText =
    lang === 'tr'
      ? (content.text_tr || content.text || '')
      : (content.text_en || content.text_tr || content.text || '');

  const keyTakeaways = content.key_takeaways || [];

  const handleStart = () => {
    onPhaseComplete({ score: 0, maxScore: 0, answers: {}, timeSpent: 0 });
  };

  // Determine label: "Fact" for <=4 items, "Item" for >4
  const itemLabel = keyTakeaways.length <= 4
    ? (t('lesson.takeaway_label_fact') || (lang === 'tr' ? 'Gerçek' : 'Fact'))
    : (t('lesson.takeaway_label_item') || (lang === 'tr' ? 'Madde' : 'Item'));

  return (
    <motion.div
      initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-6 w-full max-w-2xl mx-auto"
      data-testid={testId}
    >
      {/* ── Gradient header card ────────────────────────────────────────── */}
      <div
        className="rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-6 shadow-[0_4px_0_0_#3730a3] text-white"
        aria-label={titleText || (t('lesson.intro_header_label') || 'Ders tanıtımı')}
      >
        <div className="flex flex-col items-start gap-3">
          <span className="text-4xl" aria-hidden="true">✈️</span>
          {titleText && (
            <h2 className="text-2xl md:text-3xl font-black leading-snug" data-testid={`${testId}-title`}>
              {titleText}
            </h2>
          )}
          {bodyText && (
            <p className="text-base md:text-lg text-white/85 leading-relaxed" data-testid={`${testId}-body`}>
              {bodyText}
            </p>
          )}
          {/* Optional image inside header */}
          {content.image_url && (
            <img
              src={content.image_url}
              alt={titleText || (t('lesson.intro_image_alt') || 'Ders görseli')}
              className="w-full rounded-xl object-cover max-h-48 mt-1"
              data-testid={`${testId}-image`}
            />
          )}
        </div>
      </div>

      {/* ── Key takeaways 2×2 grid ──────────────────────────────────────── */}
      {keyTakeaways.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          aria-label={t('lesson.key_takeaways') || 'Bu derste öğrenecekleriniz'}
          data-testid={`${testId}-takeaways`}
        >
          {keyTakeaways.map((item, idx) => {
            const text =
              typeof item === 'string'
                ? item
                : lang === 'tr'
                ? (item.tr || item.en || '')
                : (item.en || item.tr || '');

            const { Icon, color, bg } = pickIconForTakeaway(text, idx);

            return (
              <motion.div
                key={idx}
                custom={idx}
                variants={shouldReduce ? {} : cardVariants}
                initial="hidden"
                animate="visible"
                className={`flex flex-col gap-2 p-4 rounded-2xl border-2 bg-white shadow-[0_2px_0_0_#E5E5E5] hover:shadow-[0_4px_0_0_#E5E5E5] transition-shadow ${bg}`}
                data-testid={`${testId}-takeaway-${idx}`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl border ${bg}`}>
                  <Icon size={20} className={color} aria-hidden="true" />
                </div>
                <p className="text-xs font-black text-[var(--text-muted)] uppercase tracking-wide">
                  {itemLabel} #{idx + 1}
                </p>
                <p className="text-sm font-semibold text-[var(--text-main)] leading-snug">{text}</p>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Start CTA ──────────────────────────────────────────────────── */}
      <div className="flex justify-center pt-2">
        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.97 }}
          onClick={handleStart}
          className="flex items-center gap-2 px-10 py-4 bg-[var(--brand-primary)] text-white font-black text-lg rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          aria-label={t('lesson.start') || 'Dersi Başlat'}
          data-testid={`${testId}-start`}
        >
          {t('lesson.start') || 'Başla'}
          <ChevronRight size={22} aria-hidden="true" />
        </motion.button>
      </div>
    </motion.div>
  );
}
